import React, { useEffect, useState } from "react";
import { Upload, Button, Modal, message } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import type { RcFile } from "rc-upload/lib/interface";
import { UploadOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { useFileSave, useFileDelete } from "../../hooks/api/file/hooks";
import { toBase64 } from "../../lib/utils";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface UploadImageProps {
  value?: string[] | string;
  uploadButton?: React.ReactNode;
  count?: number;
  cropImage?: {
    aspect?: number;
    shape?: "rect" | "round";
    quality?: number;
  } | null;
  cropShape?: "rect" | "round";
  disabled?: boolean;
  classs?: string;
  onChange?: (urls: string[] | string) => void;
  listType?: 'text' | 'picture' | 'picture-card';
  canReorder?: boolean; // new prop
  isFavIcon?: boolean
}

// Sortable Item wrapper
function SortableUploadListItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const modifiedChildren = React.cloneElement(children as React.ReactElement, {
    onMouseDown: (e: any) => {
      if (
        e.target.closest(".ant-upload-list-item-actions") ||
        e.target.closest(".ant-upload-list-item-card-actions") ||
        e.target.closest("[role='button']") ||
        e.target.closest("button")
      ) {
        e.stopPropagation();
      }
    },
    onClick: (e: any) => {
      if (
        e.target.closest(".ant-upload-list-item-actions") ||
        e.target.closest(".ant-upload-list-item-card-actions") ||
        e.target.closest("[role='button']") ||
        e.target.closest("button")
      ) {
        e.stopPropagation();
      }
    },
  });

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {modifiedChildren}
    </div>
  );
}

const UploadImage: React.FC<UploadImageProps> = ({
  value = [],
  uploadButton = <Button className="upload-button" icon={<UploadOutlined />} />,
  count = 1,
  cropImage = null,
  cropShape = "rect",
  disabled = false,
  classs,
  onChange,
  listType = "picture",
  canReorder = false, // default false
  isFavIcon = false
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const { mutate: save } = useFileSave({
    onSuccess: (res) => {
      setFileList((prev) => {
        const newFile: UploadFile = {
          uid: res.data._id,
          name: res.data.name,
          url: res.data.url,
          status: "done",
        };
        const newList = [...prev, newFile];
        triggerChange(newList);
        return newList;
      });
      message.success("Uploaded successfully");
    },
    onError: () => message.error("Upload failed"),
  });

  const { mutate: remove } = useFileDelete({
    onSuccess: () => message.success("Deleted successfully"),
    onError: () => message.error("Delete failed"),
  });

  const triggerChange = (files: UploadFile[]) => {
    const urls = files.map((x) => x.url as string);
    if (count === 1) onChange?.(urls[0] || "");
    else onChange?.(urls);
  };

  // Initialize fileList from value
  useEffect(() => {
    const valArray = Array.isArray(value) ? value : value ? [value] : [];
    const urlsInState = fileList.map((f) => f.url).filter(Boolean);
    if (JSON.stringify(urlsInState) !== JSON.stringify(valArray)) {
      const initialFiles = valArray.map((url, index) => ({
        uid: `init-${index}`,
        name: `Image-${index + 1}`,
        status: "done" as UploadFile["status"],
        url,
      }));
      setFileList(initialFiles);
    }
  }, [value]);

  const beforeUpload = async (file: RcFile) => {
    const isImage = file.type.startsWith("image/");
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isImage) message.error("Only image files allowed!");
    if (!isLt5M) message.error("File must be smaller than 5MB!");
    if (!isImage || !isLt5M) return Upload.LIST_IGNORE;

    const formData = new FormData();
    formData.append("file", file);
    save(formData);
    return false; // prevent antd auto-upload
  };

  const handleRemove = (file: UploadFile) => {
    if (file.url) remove({ url: file.url });
    setFileList((prev) => {
      const updated = prev.filter((f) => f.uid !== file.uid);
      triggerChange(updated);
      return updated;
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = await toBase64(file.originFileObj);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url?.split("/").pop() || "");
  };

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fileList.findIndex((f) => f.uid === active.id);
    const newIndex = fileList.findIndex((f) => f.uid === over.id);
    const newList = arrayMove(fileList, oldIndex, newIndex);
    setFileList(newList);
    triggerChange(newList);
  };

  const itemRender = (originNode: React.ReactNode, file: UploadFile, currFileList: UploadFile[]) => {
    if (canReorder && currFileList.length > 1) {
      return <SortableUploadListItem id={file.uid}>{originNode}</SortableUploadListItem>;
    }
    return originNode;
  };

  const uploadProps: UploadProps = {
    accept: "image/*",
    fileList,
    onRemove: handleRemove,
    onPreview: handlePreview,
    beforeUpload,
    listType,
    disabled,
    maxCount: count,
    className: classs,
    itemRender: canReorder ? itemRender : undefined,
  };
  const uploadComponent = cropImage ? (
    <ImgCrop
      {...cropImage}
    >
      <Upload {...uploadProps}>{fileList.length >= count ? null : uploadButton}</Upload>
    </ImgCrop>
  ) : (
    <Upload {...uploadProps}>{fileList.length >= count ? null : uploadButton}</Upload>
  );

  const content = (
    <>
      {canReorder && fileList.length > 1 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fileList.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
            {uploadComponent}
          </SortableContext>
        </DndContext>
      ) : (
        uploadComponent
      )}
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
        <img alt="preview" style={{ width: "100%" }} src={previewImage || "/placeholder.svg"} />
      </Modal>
    </>
  );

  return content;
};

export default UploadImage;
